// Types for the TradeGrid Admin Auth service (api-admin-auth).

export interface PaginationMeta {
  total: number
  count: number
  per_page: number
  current_page: number
  total_pages: number
}

/** Every response from the admin-auth service is wrapped in this envelope. */
export interface ServerResponse<T> {
  type: string
  code: number
  message: string
  data: T
  meta?: { pagination?: PaginationMeta } | null
}

// Platform enum. These are the only values the API accepts.
export type Platform = 'boostxpress' | 'tradegrid' | 'terminalone' | 'analytics' | 'auth'

export const PLATFORMS: Platform[] = [
  'tradegrid',
  'boostxpress',
  'terminalone',
  'analytics',
  'auth',
]

// Environment enum.
export type PlatformEnvironment = 'development' | 'staging' | 'production'

export const PLATFORM_ENVIRONMENTS: PlatformEnvironment[] = ['development', 'staging', 'production']

export interface PlatformGrant {
  platform: Platform
  environment: PlatformEnvironment
}

// Roles are server-defined records, created and renamed via /admin/roles.
export interface Role {
  id: number
  name: string
  description?: string | null
  createdAt: string
}

export const ADMIN_SCOPE = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  DEVELOPER: 'DEVELOPER',
} as const

export interface AdminUser {
  id: number
  email: string
  firstName?: string | null
  lastName?: string | null
  isDisabled: boolean
  disabledAt?: string | null
  lastLoginAt?: string | null
  roles: string[]
  platforms: PlatformGrant[]
  createdAt: string
}

// Single call returning the roles, platforms and environments to choose from.
export interface AdminMetadata {
  roles: Role[]
  platforms: Platform[]
  environments: PlatformEnvironment[]
}

// GET /auth/me — the signed-in admin, used to gate write actions in the UI.
export type CurrentAdmin = AdminUser

// Request payloads

// Only email and roleNames are required.
export interface CreateAdminUserRequest {
  email: string
  roleNames: string[]
  firstName?: string
  lastName?: string
  platforms?: PlatformGrant[]
}

export interface UpdateUserRolesRequest {
  roleNames: string[]
}

export interface UpdateUserPlatformsRequest {
  platforms: PlatformGrant[]
}

export interface CreateRoleRequest {
  name: string
  description?: string
}

export interface UpdateRoleRequest {
  name?: string
  description?: string
}

export interface AdminUserListParams {
  page?: number
  limit?: number
  search?: string
  disabled?: boolean
}

// Audit log (read-only, SUPER_ADMIN only), no UI yet.

export type AuditAction =
  | 'USER_CREATED'
  | 'USER_DISABLED'
  | 'USER_ENABLED'
  | 'USER_ROLES_UPDATED'
  | 'PLATFORM_ACCESS_UPDATED'
